import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { 
  Settings as SettingsIcon, 
  Store, 
  Palette, 
  Clock, 
  Sparkles,
  Loader2,
  Save,
  Upload,
  Link as LinkIcon,
  Crown,
  Paintbrush,
  Check,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Star,
  Lock,
  LayoutGrid,
  List,
  Utensils
} from 'lucide-react';

const THEMES = {
  moderne: {
    name: "Moderne",
    description: "Clean et minimaliste",
    preview: {
      bg: "#FAFAFA",
      card: "#FFFFFF",
      accent: "#F97316",
      text: "#111827"
    }
  },
  elegant: {
    name: "Élégant",
    description: "Luxueux et raffiné",
    preview: {
      bg: "#1A1A1A",
      card: "#2D2D2D",
      accent: "#D4AF37",
      text: "#FFFFFF"
    }
  },
  rustique: {
    name: "Rustique",
    description: "Chaleureux et authentique",
    preview: {
      bg: "#FDF5E6",
      card: "#FFFAF0",
      accent: "#8B4513",
      text: "#3E2723"
    }
  },
  ocean: {
    name: "Océan",
    description: "Frais et apaisant",
    preview: {
      bg: "#F0F9FF",
      card: "#FFFFFF",
      accent: "#0891B2",
      text: "#0C4A6E"
    }
  },
  nature: {
    name: "Nature",
    description: "Bio et écologique",
    preview: {
      bg: "#F0FDF4",
      card: "#FFFFFF",
      accent: "#16A34A",
      text: "#14532D"
    }
  },
  neon: {
    name: "Néon",
    description: "Moderne et dynamique",
    preview: {
      bg: "#0F0F0F",
      card: "#1A1A1A",
      accent: "#E11D48",
      text: "#FFFFFF"
    }
  },
  vintage: {
    name: "Vintage",
    description: "Rétro et nostalgique",
    preview: {
      bg: "#FFFBEB",
      card: "#FEF3C7",
      accent: "#B45309",
      text: "#78350F"
    }
  },
  minuit: {
    name: "Minuit",
    description: "Sombre et mystérieux",
    preview: {
      bg: "#030712",
      card: "#111827",
      accent: "#8B5CF6",
      text: "#F9FAFB"
    }
  },
  cafe: {
    name: "Café",
    description: "Cosy et convivial",
    preview: {
      bg: "#FEF7EE",
      card: "#FFFFFF",
      accent: "#7C2D12",
      text: "#431407"
    }
  },
  pastel: {
    name: "Pastel",
    description: "Doux et délicat",
    preview: {
      bg: "#FDF2F8",
      card: "#FFFFFF",
      accent: "#EC4899",
      text: "#831843"
    }
  }
};

const Settings = () => {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    logo_url: '',
    primary_color: '#F97316',
    opening_hours: {},
    phone: '',
    address: '',
    facebook_url: '',
    instagram_url: '',
    google_review_link: '',
    tripadvisor_review_link: '',
    menu_display_mode: 'carousel',
    halal: false,
    cuisine_type: '',
    restaurant_categories: [],
    availableCategories: [],
    newCategory: '',
    ai_enabled: true,
    theme: 'moderne',
    latitude: '',
    longitude: '',
    ordering_enabled: false,
    payment_mode: 'on_site',
    stripe_publishable_key: '',
    stripe_secret_key: ''
  });

  useEffect(() => {
    fetchRestaurant();
    // Fetch available platform categories
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/categories`)
      .then(res => res.json())
      .then(cats => setForm(prev => ({ ...prev, availableCategories: cats })))
      .catch(() => {});
  }, []);

  const fetchRestaurant = async () => {
    try {
      const response = await api.get('/restaurant');
      setRestaurant(response.data);
      setForm({
        name: response.data.name || '',
        description: response.data.description || '',
        logo_url: response.data.logo_url || '',
        primary_color: response.data.primary_color || '#F97316',
        opening_hours: response.data.opening_hours || {},
        phone: response.data.phone || '',
        address: response.data.address || '',
        facebook_url: response.data.facebook_url || '',
        instagram_url: response.data.instagram_url || '',
        google_review_link: response.data.google_review_link || '',
        tripadvisor_review_link: response.data.tripadvisor_review_link || '',
        menu_display_mode: response.data.menu_display_mode || 'carousel',
        halal: response.data.halal || false,
        cuisine_type: response.data.cuisine_type || '',
        restaurant_categories: response.data.restaurant_categories || [],
        ai_enabled: response.data.ai_enabled !== false,
        theme: response.data.theme || 'moderne',
        latitude: response.data.latitude || '',
        longitude: response.data.longitude || '',
        ordering_enabled: response.data.ordering_enabled || false,
        payment_mode: response.data.payment_mode || 'on_site',
        stripe_publishable_key: response.data.stripe_publishable_key || '',
        stripe_secret_key: response.data.stripe_secret_key || ''
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = async (themeKey) => {
    if (restaurant?.subscription_plan !== 'premium') {
      toast.error('Les thèmes personnalisés sont disponibles avec l\'abonnement Premium');
      return;
    }
    
    setSavingTheme(true);
    try {
      await api.put('/restaurant/theme', { theme: themeKey });
      setForm(prev => ({ ...prev, theme: themeKey }));
      setRestaurant(prev => ({ ...prev, theme: themeKey }));
      toast.success(`Thème "${THEMES[themeKey].name}" appliqué`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de thème');
    } finally {
      setSavingTheme(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { availableCategories, newCategory, ...saveData } = form;
      await api.put('/restaurant', saveData);
      toast.success('Paramètres enregistrés');
      fetchRestaurant();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDisplayModeChange = async (checked) => {
    const mode = checked ? 'carousel' : 'list';
    setForm(prev => ({ ...prev, menu_display_mode: mode }));
    try {
      const { availableCategories: _a, newCategory: _n, ...modeData } = form;
      await api.put('/restaurant', { ...modeData, menu_display_mode: mode });
      toast.success(mode === 'carousel' ? 'Carrousel activé' : 'Liste classique activée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      setForm(prev => ({ ...prev, menu_display_mode: checked ? 'list' : 'carousel' }));
    }
  };

  const colors = [
    { name: 'Orange', value: '#F97316' },
    { name: 'Vert', value: '#10B981' },
    { name: 'Bleu', value: '#3B82F6' },
    { name: 'Violet', value: '#8B5CF6' },
    { name: 'Rose', value: '#F43F5E' },
    { name: 'Jaune', value: '#EAB308' },
    { name: 'Rouge', value: '#EF4444' },
    { name: 'Gris', value: '#6B7280' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <SettingsIcon className="h-8 w-8 text-orange-500" />
            Paramètres
          </h1>
          <p className="text-gray-500 mt-1">
            Personnalisez votre restaurant
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enregistrement...</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Enregistrer</>
          )}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Restaurant Info */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Store className="h-5 w-5 text-blue-500" />
              Informations générales
            </CardTitle>
            <CardDescription>
              Nom et description de votre établissement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du restaurant</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Mon Restaurant"
                data-testid="settings-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Décrivez votre restaurant..."
                rows={4}
                data-testid="settings-description"
              />
            </div>
          </CardContent>
        </Card>

        {/* Branding */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-500" />
              Apparence
            </CardTitle>
            <CardDescription>
              Logo et couleur de votre menu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Logo du restaurant</Label>
              <Tabs defaultValue="upload" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload" className="text-sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Importer
                  </TabsTrigger>
                  <TabsTrigger value="url" className="text-sm">
                    <LinkIcon className="h-4 w-4 mr-2" />
                    URL
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="upload" className="mt-3">
                  <ImageUpload
                    value={form.logo_url}
                    onChange={(url) => setForm(prev => ({ ...prev, logo_url: url }))}
                    aspectRatio="square"
                    placeholder="Importer votre logo"
                  />
                </TabsContent>
                <TabsContent value="url" className="mt-3 space-y-2">
                  <Input
                    id="logo"
                    value={form.logo_url}
                    onChange={(e) => setForm(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="https://..."
                    data-testid="settings-logo"
                  />
                  {form.logo_url && !form.logo_url.startsWith('/api/') && (
                    <div className="p-4 bg-gray-50 rounded-xl flex items-center justify-center">
                      <img 
                        src={form.logo_url} 
                        alt="Logo preview" 
                        className="max-h-20 max-w-full object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
            <div className="space-y-2">
              <Label>Couleur principale</Label>
              <div className="grid grid-cols-4 gap-2">
                {colors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setForm(prev => ({ ...prev, primary_color: color.value }))}
                    className={`h-12 rounded-lg transition-all ${
                      form.primary_color === color.value 
                        ? 'ring-2 ring-offset-2 ring-gray-900 scale-105' 
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                    data-testid={`color-${color.name.toLowerCase()}`}
                  />
                ))}
              </div>
              
              {/* Custom color picker */}
              <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                <Label className="text-sm text-gray-600 mb-2 block">Ou choisissez une couleur personnalisée</Label>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="color"
                      value={form.primary_color}
                      onChange={(e) => setForm(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                      data-testid="custom-color-picker"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={form.primary_color}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(val) || val === '') {
                          setForm(prev => ({ ...prev, primary_color: val || '#' }));
                        }
                      }}
                      placeholder="#F97316"
                      className="font-mono text-sm"
                      data-testid="custom-color-input"
                    />
                  </div>
                  <div 
                    className="w-12 h-12 rounded-lg border-2 border-gray-200"
                    style={{ backgroundColor: form.primary_color }}
                  />
                </div>
              </div>
              
              <p className="text-xs text-gray-500">
                Couleur sélectionnée: {form.primary_color}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Opening Hours */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-emerald-500" />
              Horaires d'ouverture
            </CardTitle>
            <CardDescription>
              Informez vos clients de vos horaires
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((day) => (
                <div key={day} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-gray-600">{day}</span>
                  <Input
                    type="text"
                    placeholder="11h30 - 14h30, 18h30 - 22h30"
                    value={form.opening_hours[day.toLowerCase()] || ''}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      opening_hours: { ...prev.opening_hours, [day.toLowerCase()]: e.target.value }
                    }))}
                    className="flex-1 h-9 text-sm"
                    data-testid={`hours-${day.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Phone className="h-5 w-5 text-blue-500" />
              Coordonnées
            </CardTitle>
            <CardDescription>
              Téléphone et adresse pour vos clients
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Numéro de téléphone</Label>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400" />
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                  data-testid="settings-phone"
                />
              </div>
              <p className="text-xs text-gray-500">
                Sera cliquable sur mobile pour appeler directement
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-400 mt-3" />
                <Textarea
                  id="address"
                  value={form.address}
                  onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Rue de la Gastronomie, 75001 Paris"
                  rows={2}
                  data-testid="settings-address"
                />
              </div>
              <p className="text-xs text-gray-500">
                Sera cliquable pour ouvrir dans Google Maps
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Coordonnées GPS <span className="text-xs text-gray-400 font-normal">(pour la carte Explorer)</span></Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="latitude" className="text-xs text-gray-500">Latitude</Label>
                  <Input id="latitude" type="number" step="any" value={form.latitude} onChange={(e) => setForm(prev => ({ ...prev, latitude: e.target.value }))}
                    placeholder="-21.115" data-testid="settings-latitude" />
                </div>
                <div>
                  <Label htmlFor="longitude" className="text-xs text-gray-500">Longitude</Label>
                  <Input id="longitude" type="number" step="any" value={form.longitude} onChange={(e) => setForm(prev => ({ ...prev, longitude: e.target.value }))}
                    placeholder="55.536" data-testid="settings-longitude" />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Trouvez vos coordonnées sur <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="text-orange-500 underline">Google Maps</a> (clic droit → coordonnées)
              </p>
            </div>

            {/* Commandes en ligne */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">Commandes en ligne</Label>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Activer les commandes</p>
                    <p className="text-xs text-gray-500">Les clients pourront commander depuis le QR code de table</p>
                  </div>
                  <Switch checked={form.ordering_enabled}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, ordering_enabled: checked }))}
                    data-testid="ordering-enabled-toggle" />
                </div>
                {form.ordering_enabled && (
                  <div className="space-y-2 pl-1">
                    <Label className="text-sm text-gray-600">Mode de paiement</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { value: 'on_site', label: 'Paiement sur place', desc: 'Le client paie en caisse' },
                        { value: 'stripe', label: 'Paiement en ligne (Stripe)', desc: 'Le client paie en ligne via Stripe' },
                        { value: 'both', label: 'Les deux', desc: 'Le client choisit son mode de paiement' }
                      ].map(opt => (
                        <button key={opt.value} type="button"
                          onClick={() => setForm(prev => ({ ...prev, payment_mode: opt.value }))}
                          className={`text-left p-3 rounded-xl border-2 transition-all ${form.payment_mode === opt.value ? 'border-orange-400 bg-orange-50' : 'border-gray-100 hover:border-gray-200'}`}
                          data-testid={`payment-mode-${opt.value}`}>
                          <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                          <p className="text-xs text-gray-500">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {form.ordering_enabled && (form.payment_mode === 'stripe' || form.payment_mode === 'both') && (
                  <div className="space-y-3 pl-1 mt-3 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                    <p className="text-sm font-medium text-purple-800">Configuration Stripe</p>
                    <p className="text-xs text-purple-600">Créez un compte sur <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">stripe.com</a>, puis copiez vos clés depuis Développeurs → Clés API</p>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-gray-600">Clé publique (pk_...)</Label>
                        <Input value={form.stripe_publishable_key} onChange={e => setForm(prev => ({ ...prev, stripe_publishable_key: e.target.value }))}
                          placeholder="pk_live_..." className="text-sm font-mono" data-testid="stripe-publishable-key" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Clé secrète (sk_...)</Label>
                        <Input type="password" value={form.stripe_secret_key} onChange={e => setForm(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                          placeholder="sk_live_..." className="text-sm font-mono" data-testid="stripe-secret-key" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t border-gray-100 pt-4 mt-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">Réseaux sociaux</Label>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="facebook" className="text-sm text-gray-600">Facebook</Label>
                  <div className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-[#1877F2]" />
                    <Input
                      id="facebook"
                      type="url"
                      value={form.facebook_url}
                      onChange={(e) => setForm(prev => ({ ...prev, facebook_url: e.target.value }))}
                      placeholder="https://facebook.com/votre-page"
                      data-testid="settings-facebook"
                    />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="instagram" className="text-sm text-gray-600">Instagram</Label>
                  <div className="flex items-center gap-2">
                    <Instagram className="h-4 w-4 text-[#E4405F]" />
                    <Input
                      id="instagram"
                      type="url"
                      value={form.instagram_url}
                      onChange={(e) => setForm(prev => ({ ...prev, instagram_url: e.target.value }))}
                      placeholder="https://instagram.com/votre-compte"
                      data-testid="settings-instagram"
                    />
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                S'ouvriront dans l'application mobile si installée
              </p>
            </div>
            
            {/* Review Links Section */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-500" />
                Liens pour les avis clients
                {restaurant?.subscription_plan === 'starter' && (
                  <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Essentiel+</Badge>
                )}
              </Label>
              
              {restaurant?.subscription_plan === 'starter' ? (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Fonctionnalité disponible à partir du forfait Essentiel
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Collectez des avis Google & Tripadvisor automatiquement
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = '/billing'}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    Passer à Essentiel
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    Ajoutez vos liens pour encourager vos clients à laisser un avis
                  </p>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="google_review" className="text-sm text-gray-600">Google (Avis Google)</Label>
                      <div className="flex items-center gap-2">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        <Input
                          id="google_review"
                          type="url"
                          value={form.google_review_link}
                          onChange={(e) => setForm(prev => ({ ...prev, google_review_link: e.target.value }))}
                          placeholder="https://g.page/r/..."
                          data-testid="settings-google-review"
                        />
                      </div>
                      <p className="text-xs text-gray-400 ml-6">
                        Trouvez votre lien dans Google Business Profile
                      </p>
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="tripadvisor_review" className="text-sm text-gray-600">Tripadvisor</Label>
                      <div className="flex items-center gap-2">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#34E0A1">
                          <circle cx="12" cy="12" r="12"/>
                          <circle cx="8.5" cy="12" r="3" fill="white"/>
                          <circle cx="15.5" cy="12" r="3" fill="white"/>
                          <circle cx="8.5" cy="12" r="1.5" fill="#34E0A1"/>
                          <circle cx="15.5" cy="12" r="1.5" fill="#34E0A1"/>
                        </svg>
                        <Input
                          id="tripadvisor_review"
                          type="url"
                          value={form.tripadvisor_review_link}
                          onChange={(e) => setForm(prev => ({ ...prev, tripadvisor_review_link: e.target.value }))}
                          placeholder="https://www.tripadvisor.fr/..."
                          data-testid="settings-tripadvisor-review"
                        />
                      </div>
                      <p className="text-xs text-gray-400 ml-6">
                        Lien vers votre page Tripadvisor
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <strong>Astuce :</strong> Un bouton flottant apparaîtra sur votre menu public pour inviter vos clients à laisser un avis.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Menu Display Mode */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <LayoutGrid className="h-4 w-4 text-orange-500" />
                Affichage du menu
              </Label>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {form.menu_display_mode === 'carousel' ? (
                    <LayoutGrid className="h-5 w-5 text-orange-500" />
                  ) : (
                    <List className="h-5 w-5 text-gray-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {form.menu_display_mode === 'carousel' ? 'Carrousel horizontal' : 'Liste verticale'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {form.menu_display_mode === 'carousel' 
                        ? 'Les plats défilent horizontalement (style Uber Eats)' 
                        : 'Les plats sont affichés en liste classique'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={form.menu_display_mode === 'carousel'}
                  onCheckedChange={handleDisplayModeChange}
                  data-testid="settings-menu-display-mode"
                />
              </div>
            </div>

            {/* Catégories du restaurant */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <Label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Utensils className="h-4 w-4 text-orange-500" />
                Catégories du restaurant
              </Label>
              <p className="text-xs text-gray-500 mb-3">Sélectionnez une ou plusieurs catégories. Elles seront visibles sur la plateforme.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {(form.availableCategories || []).map((cat) => {
                  const isSelected = (form.restaurant_categories || []).includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        const current = form.restaurant_categories || [];
                        const updated = isSelected ? current.filter(c => c !== cat) : [...current, cat];
                        const newHalal = updated.includes('Halal');
                        setForm(prev => ({ ...prev, restaurant_categories: updated, halal: newHalal }));
                        api.put('/restaurant', { ...form, restaurant_categories: updated, halal: newHalal }).then(() => {
                          toast.success(isSelected ? `${cat} retiré` : `${cat} ajouté`);
                        }).catch(() => toast.error('Erreur'));
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? cat === 'Halal' 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`cat-toggle-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Ajouter une catégorie..."
                  value={form.newCategory || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, newCategory: e.target.value }))}
                  className="h-9 text-sm rounded-lg"
                  data-testid="settings-new-category"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = (form.newCategory || '').trim();
                      if (!val) return;
                      const current = form.restaurant_categories || [];
                      if (current.includes(val)) { toast.error('Catégorie déjà ajoutée'); return; }
                      const updated = [...current, val];
                      const newHalal = updated.includes('Halal');
                      setForm(prev => ({ ...prev, restaurant_categories: updated, halal: newHalal, newCategory: '', availableCategories: [...(prev.availableCategories || []), val].filter((v, i, a) => a.indexOf(v) === i) }));
                      // Add to platform categories
                      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/categories?name=${encodeURIComponent(val)}`, { method: 'POST' });
                      api.put('/restaurant', { ...form, restaurant_categories: updated, halal: newHalal }).then(() => {
                        toast.success(`${val} ajouté`);
                      }).catch(() => toast.error('Erreur'));
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 text-sm"
                  data-testid="settings-add-category-btn"
                  onClick={() => {
                    const val = (form.newCategory || '').trim();
                    if (!val) return;
                    const current = form.restaurant_categories || [];
                    if (current.includes(val)) { toast.error('Catégorie déjà ajoutée'); return; }
                    const updated = [...current, val];
                    const newHalal = updated.includes('Halal');
                    setForm(prev => ({ ...prev, restaurant_categories: updated, halal: newHalal, newCategory: '', availableCategories: [...(prev.availableCategories || []), val].filter((v, i, a) => a.indexOf(v) === i) }));
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/categories?name=${encodeURIComponent(val)}`, { method: 'POST' });
                    api.put('/restaurant', { ...form, restaurant_categories: updated, halal: newHalal }).then(() => {
                      toast.success(`${val} ajouté`);
                    }).catch(() => toast.error('Erreur'));
                  }}
                >
                  Ajouter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Settings */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-500" />
              Intelligence Artificielle
              {restaurant?.subscription_plan === 'starter' && (
                <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Essentiel+</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Paramètres des fonctionnalités IA
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {restaurant?.subscription_plan === 'starter' ? (
              <div className="p-6 bg-gray-50 rounded-xl text-center">
                <Lock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-gray-900 mb-2">
                  Fonctionnalité Essentiel / Premium
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Passez à Essentiel pour accéder à l'assistant IA intelligent.
                </p>
                <ul className="text-sm text-gray-500 mb-4 space-y-1">
                  <li>• Génération automatique de descriptions</li>
                  <li>• Suggestion de plats du jour</li>
                  <li>• Création de promotions optimisées</li>
                </ul>
                <Button
                  onClick={() => window.location.href = '/billing'}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                >
                  Passer à Essentiel
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">Activer l'IA</p>
                    <p className="text-sm text-gray-500">
                      Recommandations et suggestions automatiques
                    </p>
                  </div>
                  <Switch
                    checked={form.ai_enabled}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, ai_enabled: checked }))}
                    data-testid="toggle-ai"
                  />
                </div>

                <div className="p-4 border border-gray-200 rounded-xl">
                  <h4 className="font-medium text-gray-900 mb-2">Fonctionnalités IA incluses :</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>✓ Génération de descriptions de plats</li>
                    <li>✓ Suggestion du plat du jour</li>
                    <li>✓ Recommandations d'upsells</li>
                    <li>✓ Création de promotions</li>
                    <li>✓ Conseils quotidiens</li>
                  </ul>
                </div>

                <p className="text-xs text-gray-500">
                  Les fonctionnalités IA sont disponibles selon votre abonnement.
                  <a href="/billing" className="text-orange-500 hover:underline ml-1">
                    Voir les plans
                  </a>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Theme Selection - Premium Feature */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Paintbrush className="h-5 w-5 text-purple-500" />
                Thème du menu public
              </CardTitle>
              <CardDescription>
                Personnalisez l'apparence de votre menu pour vos clients
              </CardDescription>
            </div>
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
              <Crown className="h-3 w-3 mr-1" />
              Premium
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {!['essentiel', 'premium'].includes(restaurant?.subscription_plan) ? (
            <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl text-center">
              <Crown className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <h3 className="font-heading font-semibold text-gray-900 mb-2">
                Fonctionnalité Essentiel / Premium
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Passez à l'abonnement Essentiel ou Premium pour personnaliser le thème de votre menu public.
              </p>
              <Button
                onClick={() => window.location.href = '/billing'}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
              >
                Voir les abonnements
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(THEMES).map(([key, theme]) => {
                const isSelected = form.theme === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleThemeChange(key)}
                    disabled={savingTheme}
                    className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? 'border-orange-500 ring-2 ring-orange-200' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`theme-${key}`}
                  >
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 bg-orange-500 text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    
                    {/* Theme Preview */}
                    <div 
                      className="h-16 rounded-lg mb-2 p-1.5 flex items-end gap-1"
                      style={{ backgroundColor: theme.preview.bg }}
                    >
                      <div 
                        className="h-6 w-6 rounded shadow-sm"
                        style={{ backgroundColor: theme.preview.card }}
                      />
                      <div 
                        className="h-8 w-8 rounded shadow-sm"
                        style={{ backgroundColor: theme.preview.card }}
                      />
                      <div 
                        className="h-4 flex-1 rounded"
                        style={{ backgroundColor: theme.preview.accent }}
                      />
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 text-sm">{theme.name}</h4>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{theme.description}</p>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restaurant Info */}
      <Card className="border-0 shadow-soft bg-gray-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <span><strong>Slug:</strong> {restaurant?.slug}</span>
            <span>•</span>
            <span><strong>Plan:</strong> {restaurant?.subscription_plan}</span>
            <span>•</span>
            <span><strong>Créé le:</strong> {new Date(restaurant?.created_at).toLocaleDateString('fr-FR')}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
